import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Button, CircularProgress, TextField } from '@mui/material';
import PostData from '../services/PostData.js';
import useApiData from '../services/useApiData.js';
import SnackbarEx from '../components/Helpers/SnackbarEx.jsx';

const ITEM_ID_CATALOG_ENDPOINT = 'api/broylesllc/shelf-inventory/item-id-catalog';
const SUBMIT_SHELF_INVENTORY_ENDPOINT = 'broylesllc/shelf-inventory/submit';
const MIN_ITEM_ID_SEARCH_LENGTH = 2;
const MAX_ITEM_ID_RESULTS = 100;

const getItemIdLabel = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return String(option.itemId || option.id || '');
};

const getItemIdKey = (option) => option?.id || getItemIdLabel(option);

const getSelectedItemPayload = (option) => ({
    itemId: getItemIdLabel(option),
    ids: option?.id ? [option.id] : [],
    skuName: option?.skuName || '',
});

const getSubmitErrorMessage = (error) => {
    const responseError = error?.response?.data?.message || error?.response?.data;

    return typeof responseError === 'string'
        ? responseError
        : 'Unable to submit shelf inventory.';
};

const sameScanValue = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

const optionMatchesScan = (option, scan) => {
    const q = String(scan || '').trim().toLowerCase();
    if (!q || !option) return false;
    const keys = [
        getItemIdLabel(option),
        option.parserItemId,
        option.macSubId != null && option.macSubId !== '' ? String(option.macSubId) : '',
    ];
    return keys.some((key) => String(key || '').trim().toLowerCase() === q);
};

export default function ShelfInventory() {
    const shelfNameRef = useRef(null);
    const itemIdInputRef = useRef(null);
    const finishingShelfRef = useRef(false);
    const lastAutoAddedQueryRef = useRef('');

    const [shelfName, setShelfName] = useState('');
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [itemIdSearch, setItemIdSearch] = useState('');
    const [debouncedItemIdSearch, setDebouncedItemIdSearch] = useState('');
    const [submitLoad, setSubmitLoad] = useState(false);
    const [openTestSnack, setOpenTestSnack] = useState({
        color: '',
        message: '',
        open: false
    });

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedItemIdSearch(itemIdSearch.trim());
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [itemIdSearch]);

    const canSearchItemIds = debouncedItemIdSearch.length >= MIN_ITEM_ID_SEARCH_LENGTH;
    const itemIdCatalogEndpoint = useMemo(() => (
        `${ITEM_ID_CATALOG_ENDPOINT}?search=${encodeURIComponent(debouncedItemIdSearch)}&limit=${MAX_ITEM_ID_RESULTS}`
    ), [debouncedItemIdSearch]);
    const { data: itemIdCatalog, error: itemIdCatalogError, load: loadingItemIdCatalog } = useApiData(
        itemIdCatalogEndpoint,
        { enabled: canSearchItemIds }
    );

    const itemIdOptions = useMemo(() => (
        Array.isArray(itemIdCatalog) ? itemIdCatalog : []
    ), [itemIdCatalog]);

    const filteredItemIdOptions = useMemo(() => {
        const q = itemIdSearch.trim();
        if (q.length < MIN_ITEM_ID_SEARCH_LENGTH) return [];
        // Don't treat a shelf-name rescan as a catalog search.
        if (sameScanValue(q, shelfName)) return [];
        // Avoid showing / acting on stale results while the next scan is still typing in.
        if (q !== debouncedItemIdSearch) return [];
        return itemIdOptions.slice(0, MAX_ITEM_ID_RESULTS);
    }, [itemIdOptions, itemIdSearch, shelfName, debouncedItemIdSearch]);

    const focusShelfName = useCallback(() => {
        requestAnimationFrame(() => {
            shelfNameRef.current?.focus();
            shelfNameRef.current?.select?.();
        });
    }, []);

    const focusItemIdInput = useCallback(() => {
        requestAnimationFrame(() => {
            itemIdInputRef.current?.focus();
        });
    }, []);

    const handleSubmit = useCallback(async ({ refocusShelf = false } = {}) => {
        if (submitLoad || !shelfName.trim() || selectedItemIds.length === 0) {
            return false;
        }

        setSubmitLoad(true);

        const payload = {
            shelfName: shelfName.trim(),
            itemIdCatalog: selectedItemIds.map(getSelectedItemPayload),
        };

        const { success, error } = await PostData(payload, SUBMIT_SHELF_INVENTORY_ENDPOINT, null);

        setSubmitLoad(false);

        if (success) {
            setShelfName('');
            setSelectedItemIds([]);
            setItemIdSearch('');
            setDebouncedItemIdSearch('');
            setOpenTestSnack({
                color: 'success',
                message: 'Shelf inventory submitted successfully.',
                open: true
            });
            if (refocusShelf) focusShelfName();
            return true;
        }

        setOpenTestSnack({
            color: 'error',
            message: getSubmitErrorMessage(error),
            open: true
        });
        return false;
    }, [submitLoad, shelfName, selectedItemIds, focusShelfName]);

    /** Rescan of the current shelf name while on Item ID = finish this shelf. */
    const tryFinishShelfFromRescan = useCallback(async () => {
        const scanned = itemIdSearch.trim();
        const shelf = shelfName.trim();
        if (!scanned || !shelf || !sameScanValue(scanned, shelf)) return false;
        if (finishingShelfRef.current || submitLoad) return false;

        if (selectedItemIds.length === 0) {
            setItemIdSearch('');
            setDebouncedItemIdSearch('');
            setOpenTestSnack({
                color: 'error',
                message: 'Scan at least one catalog item before finishing the shelf.',
                open: true,
            });
            return false;
        }

        finishingShelfRef.current = true;
        setItemIdSearch('');
        setDebouncedItemIdSearch('');
        try {
            return await handleSubmit({ refocusShelf: true });
        } finally {
            finishingShelfRef.current = false;
        }
    }, [itemIdSearch, shelfName, selectedItemIds.length, submitLoad, handleSubmit]);

    useEffect(() => {
        if (!sameScanValue(itemIdSearch, shelfName)) return;
        if (!itemIdSearch.trim() || !shelfName.trim()) return;
        tryFinishShelfFromRescan();
    }, [itemIdSearch, shelfName, tryFinishShelfFromRescan]);

    useEffect(() => {
        const q = itemIdSearch.trim();
        if (!q) {
            lastAutoAddedQueryRef.current = '';
            return;
        }
        if (loadingItemIdCatalog) return;
        if (q.length < MIN_ITEM_ID_SEARCH_LENGTH) return;
        // Wait for debounce so we never act on the previous item's results mid-scan.
        if (q !== debouncedItemIdSearch) return;
        if (sameScanValue(q, shelfName)) return;
        if (lastAutoAddedQueryRef.current === q) return;

        // Prefer an exact barcode/item-id match; otherwise only auto-add when uniquely matched.
        const exactMatches = filteredItemIdOptions.filter((option) =>
            optionMatchesScan(option, q)
        );
        const match =
            exactMatches.length === 1
                ? exactMatches[0]
                : exactMatches.length === 0 && filteredItemIdOptions.length === 1
                  ? filteredItemIdOptions[0]
                  : null;
        if (!match) return;

        lastAutoAddedQueryRef.current = q;
        setSelectedItemIds((prev) => {
            if (prev.some((s) => getItemIdKey(s) === getItemIdKey(match))) return prev;
            return [...prev, match];
        });
        // Clear input + debounce immediately so the next scan isn't wiped by stale results.
        setItemIdSearch('');
        setDebouncedItemIdSearch('');
        focusItemIdInput();
    }, [
        filteredItemIdOptions,
        loadingItemIdCatalog,
        itemIdSearch,
        debouncedItemIdSearch,
        shelfName,
        focusItemIdInput,
    ]);

    const submitDisabled = submitLoad || !shelfName.trim() || selectedItemIds.length === 0;

    const handleShelfNameKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        if (!shelfName.trim()) return;
        focusItemIdInput();
    };

    const handleItemIdKeyDown = (event) => {
        if (event.key !== 'Enter') return;
        if (!sameScanValue(itemIdSearch, shelfName)) return;
        event.preventDefault();
        event.stopPropagation();
        tryFinishShelfFromRescan();
    };

    return (
        <>
            <div className="card border-0" style={{
                height: 'calc(100vh - 56px)',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div className="card-header bg-white"
                    style={{
                        padding: '0 20px 5px 20px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        borderBottom: '1px solid #eee'
                    }}>
                    <h2 style={{ margin: 0, paddingTop: '10px' }}>Take Shelf Inventory</h2>
                    <p style={{ margin: 0 }}>
                        Scan shelf → items → scan shelf again to submit, then next shelf.
                    </p>
                </div>
                <div className="card-body" style={{
                    flex: 1,
                    overflowY: 'auto',
                    margin: 0,
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '720px',
                    }}>
                        <TextField
                            inputRef={shelfNameRef}
                            label="Shelf Name"
                            value={shelfName}
                            onChange={(event) => setShelfName(event.target.value)}
                            onKeyDown={handleShelfNameKeyDown}
                            size="small"
                            fullWidth
                            autoFocus
                            helperText="Scan shelf name, then scan catalog items. Rescan the shelf name to submit."
                        />
                        <Autocomplete
                            multiple
                            filterSelectedOptions
                            clearOnBlur={false}
                            selectOnFocus={false}
                            loading={loadingItemIdCatalog}
                            options={filteredItemIdOptions}
                            value={selectedItemIds}
                            inputValue={itemIdSearch}
                            onChange={(event, newValue) => setSelectedItemIds(newValue)}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'reset') return;
                                setItemIdSearch(newInputValue);
                            }}
                            getOptionLabel={getItemIdLabel}
                            isOptionEqualToValue={(option, value) => getItemIdKey(option) === getItemIdKey(value)}
                            filterOptions={(options) => options}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                return (
                                    <li key={key} {...rest}>
                                        <div>
                                            <div>{getItemIdLabel(option)}</div>
                                            {option?.skuName ? (
                                                <div style={{ fontSize: 12, color: '#5c6570' }}>{option.skuName}</div>
                                            ) : null}
                                        </div>
                                    </li>
                                );
                            }}
                            loadingText="Fetching item IDs..."
                            noOptionsText={
                                sameScanValue(itemIdSearch, shelfName) && itemIdSearch.trim()
                                    ? 'Shelf name detected — submitting…'
                                    : loadingItemIdCatalog
                                      ? 'Fetching item IDs'
                                      : itemIdSearch.trim().length < MIN_ITEM_ID_SEARCH_LENGTH
                                        ? `Type at least ${MIN_ITEM_ID_SEARCH_LENGTH} characters to search`
                                        : 'No item IDs found'
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={(node) => {
                                        itemIdInputRef.current = node;
                                        const paramRef = params.inputRef;
                                        if (typeof paramRef === 'function') paramRef(node);
                                        else if (paramRef) paramRef.current = node;
                                    }}
                                    label="Item ID Catalog"
                                    size="small"
                                    onKeyDown={handleItemIdKeyDown}
                                    error={Boolean(itemIdCatalogError)}
                                    helperText={
                                        itemIdCatalogError
                                            ? 'Unable to load item ID catalog.'
                                            : `Showing up to ${MAX_ITEM_ID_RESULTS} matching item IDs.`
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingItemIdCatalog && (
                                                    <CircularProgress color="inherit" size={20} />
                                                )}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="card-footer" style={{
                    padding: '16px 20px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    background: 'white'
                }}>
                    <Button
                        variant="contained"
                        loading={submitLoad}
                        disabled={submitDisabled}
                        disableElevation
                        onClick={() => handleSubmit({ refocusShelf: true })}
                        sx={{
                            backgroundColor: '#3E5C76',
                            width: '200px',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#3e5c76f0' }
                        }}
                    >
                        Submit
                    </Button>
                </div>
            </div>
            {openTestSnack.open && (
                <SnackbarEx
                    color={openTestSnack.color}
                    message={openTestSnack.message}
                    open={openTestSnack.open}
                    close={() => setOpenTestSnack({
                        color: '',
                        message: '',
                        open: false
                    })}
                />
            )}
        </>
    );
};
